// Contracts — javelin антидубль участников (2026-07-27).
// Worker: apps/api/src/javelin/dedupe.ts (+ гейт внутри setup.ts
// participantIntake). Client: apps/internal javelin island features/intake
// (предпроверка) и features/dedupe (карточка кандидатов, обход).
//
// Пороги похожести ФИО и правила нормализации телефона живут В КОДЕ
// (dedupe.ts), не в контракте: контракт описывает форму обмена, а не
// эвристику матчинга — её меняют без правки клиента.
import { z } from 'zod';

export const JavDupSignalKindSchema = z.enum(['phone', 'name', 'face']);
export const JavDupSignalLevelSchema = z.enum(['block', 'warn']);

export const JavDupSignalSchema = z.looseObject({
  kind: JavDupSignalKindSchema,
  level: JavDupSignalLevelSchema,
  pids: z.array(z.string()).default([]),
  detail: z.string().default(''),
});
export type JavDupSignal = z.infer<typeof JavDupSignalSchema>;

export const JavDupCandidateSchema = z.looseObject({
  participant_id: z.string(),
  name: z.string().default(''),
  mod_id: z.string().default(''),
  home_id: z.string().default(''),
  session_date: z.string().default(''),
  matched_on: z.enum(['phone', 'phone_tail', 'name']),
  score: z.number().default(0),
});
export type JavDupCandidate = z.infer<typeof JavDupCandidateSchema>;

export const JavDupVerdictSchema = z.enum(['clear', 'warn', 'block']);

/**
 * mode='participant' (дефолт) — визард в руках участника: ответ НЕ содержит
 * кандидатов. mode='moderator' — экран модератора, кандидаты отдаются.
 */
export const JavDupCheckReqSchema = z.object({
  name: z.string().trim().default(''),
  phone: z.string().trim().default(''),
  home_id: z.string().trim().default(''),
  mode: z.enum(['participant', 'moderator']).default('participant'),
});

export const JavDupCheckResSchema = z.looseObject({
  check_id: z.string(),
  verdict: JavDupVerdictSchema,
  signals: z.array(JavDupSignalSchema).default([]),
  candidates: z.array(JavDupCandidateSchema).default([]),
});

/** 409 от participantIntake, когда телефон уже занят и гейт в enforce. */
export const JavDupBlockedResSchema = z.looseObject({
  error: z.literal('duplicate_blocked'),
  check_id: z.string().default(''),
  signals: z.array(JavDupSignalSchema).default([]),
  candidates: z.array(JavDupCandidateSchema).default([]),
  detail: z.string().default(''),
});

export const JavDupOverrideRequestReqSchema = z.object({
  check_id: z.string().trim().min(1),
  // Причина обязательна и осмысленна: она уходит менеджеру и остаётся
  // в аудите. Односимвольные «ок» не принимаются.
  reason: z.string().trim().min(10).max(500),
});

export const JavDupOverrideRequestResSchema = z.looseObject({
  ok: z.literal(true),
  override_id: z.string(),
  status: z.string(),
});

export const JavDupOverrideStatusQuerySchema = z.object({
  check_id: z.string().trim().min(1),
});

export const JavDupOverrideStatusResSchema = z.looseObject({
  found: z.boolean(),
  override_id: z.string().default(''),
  status: z.string().default(''),
  decision_note: z.string().default(''),
  expires_at: z.string().default(''),
});

export const JavDupOverrideListQuerySchema = z.object({
  status: z.enum(['pending', 'approved', 'rejected', 'used', 'expired', 'all']).default('pending'),
});

export const JavDupOverrideItemSchema = z.looseObject({
  override_id: z.string(),
  check_id: z.string().default(''),
  created_at: z.string().default(''),
  mod_id: z.string().default(''),
  home_id: z.string().default(''),
  reason: z.string().default(''),
  status: z.string().default(''),
  decided_at: z.string().default(''),
  decision_note: z.string().default(''),
  expires_at: z.string().default(''),
  name_raw: z.string().default(''),
  // Полный номер чужого участника менеджеру не отдаётся — только маска.
  phone_masked: z.string().default(''),
  verdict: z.string().default(''),
  signals: z.array(JavDupSignalSchema).default([]),
});

export const JavDupOverrideListResSchema = z.looseObject({
  items: z.array(JavDupOverrideItemSchema).default([]),
});

export const JavDupOverrideDecideReqSchema = z.object({
  override_id: z.string().trim().min(1),
  decision: z.enum(['approved', 'rejected']),
  note: z.string().trim().max(500).default(''),
});

export const JavDupOverrideDecideResSchema = z.looseObject({
  ok: z.literal(true),
  override_id: z.string(),
  status: z.string(),
  expires_at: z.string().default(''),
});

/**
 * participantIntake — первый контракт на этот эндпоинт (правило «тронул
 * эндпоинт ⇒ контракт»). participant_id опционален: живой UI его НЕ шлёт
 * (сервер аллоцирует), путь с явным PID остался для legacy-импорта.
 */
export const JavParticipantIntakeReqSchema = z.object({
  participant_id: z.string().trim().default(''),
  home_id: z.string().trim().default(''),
  name: z.string().default(''),
  age: z.string().default(''),
  age_group: z.string().default(''),
  gender: z.string().default(''),
  weight_kg: z.string().default(''),
  height_cm: z.string().default(''),
  phone: z.string().default(''),
  ref_name: z.string().default(''),
  ref_phone: z.string().default(''),
  // Антидубль: id предпроверки и одноразовый обход, одобренный менеджером.
  check_id: z.string().trim().default(''),
  override_id: z.string().trim().default(''),
  // Фото лица, снятое на шаге визарда (лежит в faces/pending до аллокации PID).
  face_upload_id: z.string().trim().default(''),
});

export const JavParticipantIntakeResSchema = z.looseObject({
  ok: z.literal(true),
  participant_id: z.string(),
  home_id: z.string(),
  slot_index: z.number().optional(),
  is_last_on_home: z.boolean().optional(),
  warnings: z.array(JavDupSignalSchema).optional(),
});

export const JavDupGateModeSchema = z.enum(['off', 'warn', 'enforce']);

/**
 * Фото лица участника (этап A2). Загрузка двухфазная: снимок делается на
 * шаге визарда, когда PID ещё не аллоцирован, поэтому сначала pending, а
 * привязка к участнику происходит в participantIntake по `upload_id`.
 * Сама отдача фото — бинарный роут, у него только query-контракт.
 */
export const JavFaceUploadResSchema = z.looseObject({
  ok: z.literal(true),
  upload_id: z.string(),
});

export const JavFacePhotoQuerySchema = z.object({
  pid: z.string().trim().min(1),
});

export const JavGateRowSchema = z.looseObject({
  mod_id: z.string(),
  name: z.string().default(''),
  active: z.boolean().default(true),
  dup_gate_mode: JavDupGateModeSchema.default('off'),
  face_gate_mode: JavDupGateModeSchema.default('off'),
});

export const JavGatesResSchema = z.looseObject({
  items: z.array(JavGateRowSchema).default([]),
});

export const JavFaceGateSetReqSchema = z.object({
  mod_id: z.string().trim().min(1),
  mode: JavDupGateModeSchema,
});

export const JavFaceGateSetResSchema = z.looseObject({
  ok: z.literal(true),
  mod_id: z.string(),
  mode: JavDupGateModeSchema,
});

export const JavDupGateSetReqSchema = z.object({
  mod_id: z.string().trim().min(1),
  mode: JavDupGateModeSchema,
});

export const JavDupGateSetResSchema = z.looseObject({
  ok: z.literal(true),
  mod_id: z.string(),
  mode: JavDupGateModeSchema,
});
